/** @jsxImportSource @emotion/react */

import { useSelector } from 'react-redux';
import { useState } from 'react';
import { Formik } from 'formik';
import { object, string as yupString, array as yupArray } from 'yup';
import {
  ButtonStyled,
  DropDownStyled,
  CheckboxSetStyled,
  CheckboxStyled,
  TextFieldStyled,
  TextStyled
} from '@nike/nike-design-system-components';
import { Tooltip } from '@nike/eds';
import { InfoCircle, Search, Close } from '@nike/nike-design-system-icons';
import { nike } from '@nike/nike-design-tokens';

import { TranslatedCopy } from '../../intl/TranslatedCopy';
import { invitationsActions } from './actions';
import { invitationsSelectors } from './reducer';
import { requestsSelectors } from '../../requests/reducer';
import { usersSelectors } from '../users/reducer';
import { getCurrentDepartments } from '../../user/reducer';
import { locales } from '../../intl/translatedAvailableLocales';
import { useDispatchWithProgressAndSuccess } from '../../hooks/useDispatchWithProgress';

const InvitationUpdateModal = () => {
  const inviteUser = useDispatchWithProgressAndSuccess(invitationsActions.issueInvitation);

  const invitationError = useSelector(requestsSelectors.invitationError);
  const invitationInProgress = useSelector(requestsSelectors.invitationInProgress);
  const selectedAccountNumber = useSelector(state => state.user.get('userAccount')?.number);
  const departments = useSelector(state => getCurrentDepartments(state, selectedAccountNumber));
  const roles = useSelector(usersSelectors.roles);
  const selectedInvitationDepartments = useSelector(invitationsSelectors.selectedInvitationDepartments);
  const selectedInvitationEmail = useSelector(invitationsSelectors.selectedInvitationEmail);
  const selectedInvitationId = useSelector(invitationsSelectors.selectedInvitationId);
  const selectedInvitationRole = useSelector(invitationsSelectors.selectedInvitationRole);

  const [deptSearchInput, setDeptSearchInput] = useState(null);

  return (
    <Formik
      initialValues={{
        departmentsList: departments,
        departments: selectedInvitationId ? [...selectedInvitationDepartments] : [],
        email: selectedInvitationId ? selectedInvitationEmail : null,
        language: null,
        role: selectedInvitationId ? selectedInvitationRole : null
      }}
      validationSchema={object().shape({
        departments: yupArray().min(1),
        email: yupString().email().max(255).required().nullable(),
        language: yupString().required().nullable(),
        role: yupString().required().nullable()
      })}
      onSubmit={values => {
        inviteUser({
          accountNumber: +selectedAccountNumber,
          departmentNumbers: values.departments.map(department => +department),
          email: values.email,
          language: values.language,
          role: values.role
        });
      }}
    >
      {formik => (
        <form
          id='userInvitation'
          css={{ marginTop: '10px', marginBottom: '12px', opacity: invitationInProgress || invitationError ? 0.1 : 1 }}
          onSubmit={formik.handleSubmit}
        >
          <div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              width: '100%',
              '@media (min-width: 769px)': {
                flexWrap: 'nowrap'
              }
            }}
          >
            <TextFieldStyled
              id='email'
              css={{
                width: '100%',
                '@media (min-width: 769px)': {
                  width: '325px'
                }
              }}
              error={formik.touched.email && formik.errors.email}
              errorMessage={formik.errors.email}
              label={new TranslatedCopy({ id: 'global.form.email' }).toString()}
              name='email'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              readOnly={selectedInvitationId}
              value={formik.values.email}
              requiredIndicator='*'
            />
            <DropDownStyled
              id='language'
              css={{
                width: '100%',
                '@media (min-width: 769px)': {
                  marginLeft: '24px',
                  width: '325px'
                }
              }}
              error={formik.touched.language && formik.errors.language}
              errorMessage={formik.errors.language}
              label={new TranslatedCopy({ id: 'migrate.profile.language.placeholder' }).toString()}
              name='language'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              options={locales}
              value={formik.values.language}
              requiredIndicator='*'
            />
          </div>
          <div css={{ flex: 1, display: 'flex' }}>
            <DropDownStyled
              id='role'
              css={{
                '@media (min-width: 769px)': {
                  width: '325px'
                }
              }}
              error={formik.touched.role && formik.errors.role}
              errorMessage={formik.errors.role}
              label={new TranslatedCopy({ id: 'userManagement.addUser.label.role' }).toString()}
              name='role'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              options={roles.map(role => {
                return {
                  label: new TranslatedCopy({ id: `userManagement.roles.${role.toLowerCase()}` }).toString(),
                  value: role
                };
              })}
              value={formik.values.role}
              requiredIndicator='*'
            />
            <div css={{ alignSelf: 'center', marginLeft: '10px', color: 'white' }}>
              <Tooltip
                id='rolesTooltip'
                bodySlot={
                  <div css={{ lineHeight: 'initial' }}>
                    <TextStyled appearance='body3' bold css={{ paddingBottom: '14px' }}>
                      <TranslatedCopy id='userManagement.roles.tooltip.title' />
                    </TextStyled>
                    {roles.map(role => (
                      <div css={{ paddingBottom: '10px' }}>
                        <TextStyled display='inline' appearance='body3' bold>
                          <TranslatedCopy id={`userManagement.roles.${role.toLowerCase()}`} />:{' '}
                        </TextStyled>
                        <TextStyled display='inline' appearance='body3'>
                          <TranslatedCopy id={`userManagement.roles.${role.toLowerCase()}.description`} />
                        </TextStyled>
                      </div>
                    ))}
                  </div>
                }
                isDark
                enableFocus
              >
                <InfoCircle />
              </Tooltip>
            </div>
          </div>
          <div
            css={{
              paddingTop: '5px',
              paddingBottom: '10px',
              borderBottom: `1px solid ${nike.podiumCdsColorBorderPrimary}`
            }}
          >
            <TextStyled appearance='heading4' Component='h4'>
              <TranslatedCopy id='global.departments' />
            </TextStyled>
          </div>
          <div css={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
            <TextFieldStyled
              id='departmentFilter'
              css={{
                marginTop: '12px',
                minHeight: '68px',
                width: '100%',
                '@media (min-width: 769px)': {
                  width: '325px'
                }
              }}
              label={new TranslatedCopy({ id: 'global.search' }).toString()}
              name='departmentFilter'
              onChange={event => {
                formik.setFieldValue(
                  'departmentsList',
                  departments.filter(department =>
                    department.customerDepartmentName.toLowerCase().includes(event.target.value.toLowerCase())
                  )
                );
                setDeptSearchInput(event.target.value);
              }}
              value={deptSearchInput}
              PrefixIcon={<Search />}
              {...(deptSearchInput && {
                TrailingIcon: <Close />
              })}
              onIconClick={() => {
                setDeptSearchInput(null);
                formik.setFieldValue('departmentsList', departments);
              }}
            />
            <div css={{ display: 'flex', flexDirection: 'row', marginTop: '14px', marginBottom: '3px' }}>
              <ButtonStyled
                appearance='text'
                css={{
                  display: 'block',
                  borderBottomWidth: '2px',
                  borderBottomStyle: 'solid'
                }}
                onClick={() => {
                  formik.setFieldValue(
                    'departments',
                    formik.values.departmentsList.map(department => department.customerDepartmentCode)
                  );
                }}
                type='button'
              >
                <TranslatedCopy id='cart.header.button.selectAll' />
              </ButtonStyled>
              <ButtonStyled
                appearance='text'
                css={{
                  display: 'block',
                  marginLeft: '16px',
                  borderBottomWidth: '2px',
                  borderBottomStyle: 'solid'
                }}
                onClick={() => {
                  formik.setFieldValue('departments', []);
                }}
                type='button'
              >
                <TranslatedCopy id='global.clear' />
              </ButtonStyled>
            </div>
          </div>
          <CheckboxSetStyled
            id='departments'
            css={{
              '.ndsc-selection-children-container': {
                flexDirection: 'row',
                flexWrap: 'wrap'
              },
              '.ndsc-selection-children-container > div': {
                marginLeft: 0,
                marginTop: '12px',
                width: '50%',
                '@media (min-width: 769px)': {
                  width: '33%'
                }
              }
            }}
            error={formik.touched.departments && formik.errors.departments}
            errorMessage={formik.errors.departments}
            name='departments'
            onChange={event => {
              formik.setFieldValue(
                'departments',
                event.target.checked
                  ? [...formik.values.departments, event.target.value]
                  : formik.values.departments.filter(department => department !== event.target.value)
              );
            }}
            selectedValues={formik.values.departments}
          >
            {formik.values.departmentsList.map(department => (
              <CheckboxStyled
                key={department.customerDepartmentCode}
                id={department.customerDepartmentCode}
                label={department.customerDepartmentName}
                value={department.customerDepartmentCode}
              />
            ))}
          </CheckboxSetStyled>
        </form>
      )}
    </Formik>
  );
};

export default InvitationUpdateModal;
